/**
 * Reading ONE note that nothing on screen holds — shared by the personal and
 * the study-group notes facades, which differ only in WHERE the note lives
 * (`party` + `room`).
 *
 * The note is its own Y.Doc: the read acquires it (reusing the doc when an
 * editor already holds it), waits for the server's sync — bounded, so an
 * offline device degrades to what it has cached — reads, and releases.
 *
 * "Nothing came back" is two different answers, and a caller that remembers
 * the result must tell them apart: an EMPTY note (the server answered) versus
 * a read that never reached the server (`complete: false`). Remembering the
 * second as "empty" is how a tile came to say "Not generated" on a note full
 * of text.
 */
import type { JSONContent } from '../internal/editor-types';
import type { PersonalScope } from '../yjs/active-deck-docs';
import { noteFragmentToJSON } from '../yjs/hooks';
import type { NoteDoc } from '../yjs/note-doc';
import { noteDocRegistry, type NoteDocTarget } from '../yjs/note-doc-registry';

export interface NoteReadResult {
  /** The note's content; `undefined` for an empty note or an unanswered read. */
  json: JSONContent | undefined;
  /** The server answered (the doc synced): `json` is the note, not a guess. */
  complete: boolean;
  /**
   * The note's change counter at the moment it was read. A summary kept past
   * this read is stale once `version(noteId)` moves on from it.
   */
  version: number;
}

/** Resolve once the doc reports `synced`, or after `timeoutMs`. */
function waitForSynced(
  doc: { synced: boolean; onChange: (l: () => void) => () => void },
  timeoutMs: number,
): Promise<void> {
  if (doc.synced) return Promise.resolve();
  return new Promise<void>((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      off();
      clearTimeout(timer);
      resolve();
    };
    const off = doc.onChange(() => {
      if (doc.synced) finish();
    });
    const timer = setTimeout(finish, timeoutMs);
  });
}

function contentOf(doc: NoteDoc): JSONContent | undefined {
  if (!doc.hasContent()) return undefined;
  const frag = doc.peekTiptapFragment();
  return frag && frag.length > 0 ? noteFragmentToJSON(frag) : undefined;
}

export async function readNoteDoc(
  target: NoteDocTarget,
  scope: Pick<PersonalScope, 'host' | 'getToken'> | null,
  timeoutMs: number,
): Promise<NoteReadResult> {
  // Fast path: an editor (or other consumer) already holds a synced doc.
  const peeked = noteDocRegistry.peek(target);
  if (peeked?.synced) {
    return {
      json: contentOf(peeked),
      complete: true,
      version: noteDocRegistry.versionOf(target),
    };
  }
  if (!scope?.host) {
    // Nowhere to read it from: whatever a held (unsynced) doc has, unconfirmed.
    return {
      json: peeked ? contentOf(peeked) : undefined,
      complete: false,
      version: noteDocRegistry.versionOf(target),
    };
  }

  const getToken = scope.getToken;
  const handle = noteDocRegistry.acquire({
    ...target,
    host: scope.host,
    params: getToken
      ? async (): Promise<Record<string, string>> => {
          const t = await getToken();
          return t ? { token: t } : {};
        }
      : undefined,
  });
  try {
    await waitForSynced(handle.doc, timeoutMs);
    return {
      json: contentOf(handle.doc),
      complete: handle.doc.synced,
      version: noteDocRegistry.versionOf(target),
    };
  } finally {
    handle.release();
  }
}
